import {
  type FC,
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StyledMenuWrapper } from './styles';
import { type Folder } from 'store/automationsPage/automations.redux';
import { useTranslation } from 'react-i18next';
import { actions } from '../Cells';
import { colorsButton } from '__app/colors';
import { type RenderTrailingProps } from '@kizen/kds/FoldersTree';
import { MAX_FOLDER_LEVEL } from './constants';
import { Icon } from '@kizen/kds/Icon';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import { FolderDropZone } from '../FoldersDragAndDrop/FolderDropZone';
import { TABLE_ROW_HEIGHT } from '__components/Tables/Basic';

export type ActionOption = {
  value: Extract<
    keyof typeof actions,
    'delete_folder' | 'edit_folder' | 'new_automation' | 'new_folder'
  >;
  label: string;
};

const OPTION_HEIGHT = 32;

export const RenderFolderMenu: FC<
  RenderTrailingProps<Folder> & {
    onSelectAction: (
      action: ActionOption,
      folder: Folder,
      parentFolder?: Folder
    ) => void;
    scrollContainerRef: RefObject<HTMLDivElement>;
    canDelete: boolean;
  }
> = ({
  folderStructure,
  isHovered,
  setIsHovered,
  onSelectAction,
  scrollContainerRef,
  folderRef,
  isSelected,
  canDelete,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [above, setAbove] = useState(false);

  const isSelectedRef = useRef(isSelected);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const { data, parentFolder, level } = folderStructure;

  const options = useMemo(() => {
    const options: ActionOption[] = [];
    if (data.parent_folder_id) {
      options.push({ value: actions.edit_folder, label: t('Rename') });
    }
    options.push({
      value: actions.new_automation,
      label: t('New Agentic Workflow'),
    });
    if (level < MAX_FOLDER_LEVEL) {
      options.push({ value: actions.new_folder, label: t('New Folder') });
    }
    if (data.parent_folder_id && canDelete) {
      options.push({ value: actions.delete_folder, label: t('Delete') });
    }

    return options;
  }, [t, level, data.parent_folder_id, canDelete]);

  const handleOpen = (
    isOpen: boolean,
    menuRef: RefObject<HTMLDivElement>,
    event: MouseEvent<HTMLDivElement>
  ) => {
    if (menuRef?.current) {
      const { bottom } = menuRef.current.getBoundingClientRect();
      setAbove(bottom + OPTION_HEIGHT * options.length > window.innerHeight);
    }
    event.stopPropagation();
    setMenuOpen(true);
  };

  const handleClose = () => {
    setMenuOpen(false);
  };

  const handleChange = useCallback(
    (action: ActionOption) => {
      onSelectAction(action, data, parentFolder?.data);
      setIsHovered(false);
      setMenuOpen(false);
    },
    [data, onSelectAction, parentFolder, setIsHovered]
  );

  const showMenu = isHovered || menuOpen;

  useEffect(() => {
    // closes menu on scroll
    if (scrollContainerRef.current && showMenu) {
      const handleScroll = () => {
        setIsHovered(false);
        setMenuOpen(false);
      };
      const list = scrollContainerRef.current;
      list.addEventListener('scroll', handleScroll, {
        passive: true,
        once: true,
      });
      window.addEventListener('scroll', handleScroll, {
        passive: true,
        once: true,
      });
      return () => {
        list.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollContainerRef, showMenu, setIsHovered]);

  useLayoutEffect(() => {
    // if newly selected folder is out of view, scroll to it after 300ms
    // to let expand animation finish and get correct folder position
    if (isSelectedRef.current !== isSelected) {
      isSelectedRef.current = isSelected;
      clearTimeout(timeoutRef.current);

      if (isSelected) {
        timeoutRef.current = setTimeout(() => {
          if (scrollContainerRef.current && folderRef.current) {
            const { top, bottom } =
              scrollContainerRef.current.getBoundingClientRect();
            const { top: folderTop, bottom: folderBottom } =
              folderRef.current.getBoundingClientRect();

            const y =
              folderBottom > bottom
                ? folderBottom - bottom + TABLE_ROW_HEIGHT / 2
                : folderTop < top
                  ? folderTop - top - TABLE_ROW_HEIGHT / 2
                  : 0;

            if (y !== 0) {
              scrollContainerRef.current.scrollBy({
                top: y,
                behavior: 'smooth',
              });
            }
          }
        }, 300);
      }
    }
  }, [isSelected, scrollContainerRef, folderRef]);

  return (
    <FolderDropZone folderStructure={folderStructure}>
      {showMenu && (
        <StyledMenuWrapper>
          <div onClick={(event) => event.stopPropagation()}>
            <IconButtonAbsoluteMenu
              sizing="dense"
              options={options}
              title={t('Edit Folder')}
              position="right"
              color={colorsButton.iconGray}
              screenPositionAdjust={{ top: above ? -4 : 8, left: -8 }}
              onOpen={handleOpen}
              onClose={handleClose}
              onChange={handleChange}
              above={above}
            >
              <Icon
                icon="action-options-horizontal"
                size="xl"
                className="fa-rotate-90"
              />
            </IconButtonAbsoluteMenu>
          </div>
        </StyledMenuWrapper>
      )}
    </FolderDropZone>
  );
};
